import { Component, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Attachment } from 'src/app/core/models/attachment/attachment.model';
import { AttachmentApiService } from 'src/app/core/services/attachments/attachment.service';
import { AlertService } from '../../services/alert/alert.service';

@Component({
  selector: 'app-attachments-modal',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
  templateUrl: './attachments-modal.component.html',
  styleUrls: ['./attachments-modal.component.scss']
})
export class AttachmentsModalComponent implements OnChanges {
  @Input() open = false;
  @Input() entityType!: 'tenant' | 'owner' | 'key';
  @Input() entityId!: number;
  @Input() entityName!: string;
  @Output() close = new EventEmitter<void>();

  attachments: Attachment[] = [];
  uploading = false;
  loading = false;

  private api = inject(AttachmentApiService);
  private alert = inject(AlertService);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open && this.entityId) {
      this.loadAttachments();
    }
  }

  loadAttachments(): void {
    this.loading = true;
    this.api.list(this.entityType, this.entityId).subscribe({
      next: attachments => {
        this.attachments = attachments;
        this.loading = false;
      },
      error: () => {
        this.alert.error('Falha ao carregar anexos');
        this.loading = false;
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    this.uploading = true;

    this.api.upload(this.entityType, this.entityId, file).subscribe({
      next: attachment => {
        this.attachments = [attachment, ...this.attachments];
        this.uploading = false;
        this.alert.success('Anexo adicionado com sucesso!');
        input.value = '';
      },
      error: err => {
        this.uploading = false;
        const message = err?.message || 'Falha ao enviar anexo';
        this.alert.error(message);
        input.value = '';
      }
    });
  }

  downloadAttachment(attachment: Attachment): void {
    this.api.download(attachment.id).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.name;
        a.click();
        URL.revokeObjectURL(url);
        this.alert.success('Download iniciado');
      },
      error: () => {
        this.alert.error('Falha ao baixar anexo');
      }
    });
  }

  deleteAttachment(attachment: Attachment): void {
    if (!confirm(`Deseja realmente excluir "${attachment.name}"?`)) {
      return;
    }

    this.api.remove(attachment.id).subscribe({
      next: () => {
        this.attachments = this.attachments.filter(a => a.id !== attachment.id);
        this.alert.success('Anexo excluído com sucesso!');
      },
      error: () => {
        this.alert.error('Falha ao excluir anexo');
      }
    });
  }

  getFileIcon(mimeType: string): string {
    if (mimeType.startsWith('image/')) return 'fas fa-file-image';
    if (mimeType.includes('pdf')) return 'fas fa-file-pdf';
    if (mimeType.includes('word')) return 'fas fa-file-word';
    return 'fas fa-file';
  }

  formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  formatDate(isoDate: string): string {
    return new Date(isoDate).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
}
